import { ChatFileResponse } from '@api/generated/internal/internalApi'
import { PredictionSSE } from '@api/hooks/useSessionSSE'
import { MESSAGE_TYPE_FINAL_ANSWER_STREAM, MESSAGE_TYPE_INTERMEDIATE_ANSWER_STREAM, MessageType } from '@constants/agentTypes'
import { InternalMessage, QueuedMessageUpdate, WebsocketMessageData as InferenceMessageData } from '@slices/userMessage'

type StreamStartData = {
  isIntermediateAnswer?: boolean
  runId?: string
  parentRunId?: string
}

type StreamTokenData = {
  token: string
  runId?: string
  parentRunId?: string
}

export type InferenceFilesEventData = {
  /** `name` is the event's spelling of ChatFileResponse.fileName; the rest matches it. */
  files: (Omit<ChatFileResponse, 'fileName' | 'chatflowId' | 'sessionId' | 'order'> & { name: string })[]
}

export type ParsedInferenceSSEEvent =
  | { type: 'connected' }
  | { type: 'streamStart'; message: InternalMessage }
  | { type: 'streamToken'; token: string; data: InferenceMessageData }
  | { type: 'message'; message: unknown }
  | { type: 'files'; data: InferenceFilesEventData }
  | { type: 'videoLink'; url?: string }
  | { type: 'videoError'; message?: string }
  | { type: 'messageStatus'; update: QueuedMessageUpdate }
  | { type: 'sessionState'; requestMessageId?: string; streamBuffer?: string; queuedMessages: QueuedMessageUpdate[] }
  | { type: 'ignored'; sourceType: string }

export function parseInferenceSSEEvent(event: PredictionSSE): ParsedInferenceSSEEvent {
  switch (event.type) {
    case 'connected':
      return { type: 'connected' }

    case 'start': {
      const data = event.data as StreamStartData
      const messageType: MessageType = data?.isIntermediateAnswer
        ? MESSAGE_TYPE_INTERMEDIATE_ANSWER_STREAM
        : MESSAGE_TYPE_FINAL_ANSWER_STREAM

      return {
        type: 'streamStart',
        message: {
          value: '',
          type: messageType,
          ...(event.requestMessageId ? { requestMessageId: event.requestMessageId } : {}),
          data: data as InferenceMessageData,
        },
      }
    }

    case 'token': {
      const data = event.data as StreamTokenData

      return {
        type: 'streamToken',
        token: data.token,
        data: {
          ...data,
          ...(event.requestMessageId ? { requestMessageId: event.requestMessageId } : {}),
        } as InferenceMessageData,
      }
    }

    case 'message':
      return {
        type: 'message',
        message:
          event.data && typeof event.data === 'object'
            ? {
                ...event.data,
                ...(event.requestMessageId ? { requestMessageId: event.requestMessageId } : {}),
              }
            : event.data,
      }

    case 'files':
      return { type: 'files', data: event.data as InferenceFilesEventData }

    case 'videoLink': {
      const data = event.data as { videoLink?: string } | string
      return { type: 'videoLink', url: typeof data === 'string' ? data : data?.videoLink }
    }

    case 'videoError': {
      const data = event.data as { error?: string } | string
      return { type: 'videoError', message: typeof data === 'string' ? data : data?.error }
    }

    case 'message:status':
      return { type: 'messageStatus', update: event.data as QueuedMessageUpdate }

    case 'session:state':
      return {
        type: 'sessionState',
        ...(event.requestMessageId ? { requestMessageId: event.requestMessageId } : {}),
        streamBuffer: event.streamBuffer,
        queuedMessages: event.queuedMessages ?? [],
      }

    case 'end':
    case 'sourceDocuments':
    case 'error':
      return { type: 'ignored', sourceType: event.type }

    default:
      return { type: 'ignored', sourceType: event.type }
  }
}
